'use client';

import NextImage from 'next/image';
import { CustomImage as Image } from '..';
import ChevronRight from 'assets/images/chevronRight.svg';
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './ProductCardMerch.module.css';

import { useCartStore, useShallow, DotSpinner, useModal } from '..';
import { useTranslations } from 'next-intl';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { getTaxString } from 'components/shared/Cart/utils';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { useClickOutside } from 'hooks/dom/useClickOutside';
import { MerchSizes } from 'content/products/merch/constants';
import {
  CurrencySymbol,
  getCurrencySymbolFromEnum,
  useFormatPrice,
} from 'hooks/pricing/useFormattedPrice';
import { ProductType } from 'components/shared/Cart/types';
import { Currency } from 'utils/Currency';
import { MerchClothingSizingModal } from 'components/shared/MerchClothingSizingModal';
import {
  ShopProductCardRoot,
  ShopCardBody,
  ShopCardBottomSheet,
  ShopCardFooterRow,
  ShopCardMedia,
  ShopCardMediaDimBackdrop,
  ShopCardPriceCluster,
  ShopCardPrimaryCta,
  ShopCardSpecSection,
  ShopCardTag,
  ShopCardTagText,
} from './shopProductCard';

export const MerchSizing = ({ className }: { className?: string }) => {
  const ModalClient = useModal();
  const t = useTranslations('ring');

  const showBookCallModal = () => {
    ModalClient.set(<MerchClothingSizingModal />);
    ModalClient.setCloseButtonTheme('light');
    ModalClient.show();
  };

  return (
    <button
      className={[styles.merchSizing, className, 'e-sizing']
        .filter(Boolean)
        .join(' ')}
      onClick={showBookCallModal}
    >
      {t('ring.shop.sizingChart')}
      <NextImage src={ChevronRight} alt="chevron_right" width={7} height={12} />
    </button>
  );
};

export const ProductCardMerch = (
  {
    className,
    productType,
    merchData,
    currency,
    tag,
  }: {
    className?: string;
    imgSrc?: HTMLImageElement;
    productType: ProductType;
    merchData: Record<
      string,
      string | Partial<Record<MerchSizes, Record<string, string>>>
    >;
    taxes: boolean;
    currency: Currency;
    tag?: 'new' | 'popular';
  }) => {
    const CartClient = useCartStore(
      useShallow((s) => ({
        store: s.store,
        taxString: s.taxString,
        addToCart: s.addToCart,
      })),
    );
    const { store: regionStore } = useRegionLocale();
    const resolvedStore = (CartClient.store ?? regionStore) as ShopifyStore;
    const tCart = useTranslations('cart');
    const resolvedTaxString =
      CartClient.taxString || getTaxString(resolvedStore, tCart);
    const [showSizes, setShowSizes] = useState(false);

    const [selectedSize, setSelectedSize] = useState<MerchSizes | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const hoverOut = useCallback(() => {
      if (showSizes) {
        setTimeout(() => {
          setShowSizes(false);
        }, 200);
      }
    }, [showSizes]);
    const formatPriceWithComma = useFormatPrice();

    useClickOutside(ref, hoverOut, showSizes);

    const handleAddToCart = async () => {
      const variantId = (
        merchData?.size as Record<MerchSizes, Record<string, string>>
      )?.[selectedSize as MerchSizes].variantId;

      if (!variantId) return;

      try {
        setIsLoading(true);
        await CartClient.addToCart([
          {
            shopifyVariantId: variantId,
            productType: productType,
          },
        ]);
      } finally {
        setIsLoading(false);
        hoverOut();
      }
    };

    //reset
    useEffect(() => {
      setSelectedSize(null);
    }, [showSizes]);

    // Check if birthday discount is active
    const hasBirthdayDiscount =
      merchData.originalPrice &&
      merchData.discountPercentage &&
      parseFloat(merchData.discountPercentage as string) > 0;

    return (
      <ShopProductCardRoot
        className={[styles.root, className].filter(Boolean).join(' ')}
      >
        <ShopCardMedia $background="white" onClick={() => setShowSizes(false)}>
          {merchData?.imgSrc && (
            <Image
              src={merchData.imgSrc as string}
              alt={merchData?.title as string}
              height={448}
              width={300}
              style={{
                width: '100%',
                objectFit: 'cover',
              }}
            />
          )}
        </ShopCardMedia>
        <ShopCardBody>
          <ShopCardSpecSection>
            <h3 className="title">{merchData?.title as string} </h3>
            {merchData?.description && (
              <p className="description">{merchData?.description as string}</p>
            )}
            <br />
          </ShopCardSpecSection>
          <ShopCardFooterRow
            aria-hidden={showSizes}
            style={showSizes ? { visibility: 'hidden' } : undefined}
          >
              <ShopCardPriceCluster>
                {hasBirthdayDiscount && (
                  <span className="striked">
                    {CurrencySymbol[currency as Currency]}
                    {formatPriceWithComma(
                      parseFloat(merchData?.originalPrice as string),
                    )}
                  </span>
                )}
                {CurrencySymbol[currency as Currency]}
                {formatPriceWithComma(merchData?.price as number)}
                <span> {resolvedTaxString}</span>
              </ShopCardPriceCluster>
              <ShopCardPrimaryCta
                className={isLoading ? 'quickAdd disable' : 'quickAdd'}
                data-product={merchData.title as string}
                data-buttontype="show size"
                onClick={() => {
                  setShowSizes(true);
                }}
                disabled={isLoading}
              >
                {tCart('sharedCart.upsell.addToCart')}
              </ShopCardPrimaryCta>
          </ShopCardFooterRow>
        </ShopCardBody>
        {tag ? <ShopCardTag><ShopCardTagText tag={tag} /></ShopCardTag> : null}
        <ShopCardMediaDimBackdrop className={showSizes ? 'open' : ''} />
        <ShopCardBottomSheet
          className={`sizeOverlay ${showSizes ? 'open' : ''} ${styles.bottomSheet}`}
          ref={ref}
          aria-hidden={!showSizes}
        >
          <div className="size">
            <div className="sizeHead">
              <div className="heading">{tCart('sharedCart.upsell.size')}</div>{' '} <MerchSizing />
            </div>
            <div className="sizes">
              {Object.keys(merchData?.size as MerchSizes).map((item, idx) => (
                <button
                  key={idx}
                  data-product={merchData.title}
                  data-buttontype="select size"
                  className={
                    selectedSize === (item as unknown as MerchSizes)
                      ? 'selectedMerch'
                      : ''
                  }
                  onClick={() => {
                    setSelectedSize(item as unknown as MerchSizes);
                  }}
                  aria-pressed={selectedSize === (item as unknown as MerchSizes)}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
          <button
            className="addToCart"
            disabled={!selectedSize || isLoading}
            onClick={handleAddToCart}
            data-product={merchData.title}
            data-buttontype="add to cart"
            data-variantId={
              selectedSize
                ? (
                    merchData?.size as Record<
                      MerchSizes,
                      Record<string, string>
                    >
                  )?.[selectedSize as MerchSizes].variantId
                : ''
            }
            aria-busy={isLoading}
          >
            {isLoading ? (
              <DotSpinner color="#FFFFFF" />
            ) : (
              <>
                {tCart('sharedCart.upsell.addToCartFor', {
                  price: `${getCurrencySymbolFromEnum(currency as Currency)}${formatPriceWithComma(merchData?.price as number)}`,
                })}
              </>
            )}
          </button>
        </ShopCardBottomSheet>
      </ShopProductCardRoot>
    );
};
